/* @img-migrated */
import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { ArrowRight, Target, Eye, Compass, ChevronRight, ExternalLink, Play } from 'lucide-react';
import { Button } from '../components/ui/button';
import LeadershipSignature from '../components/LeadershipSignature';

import Img from '../components/Img';
const LOGO_WHITE = '/brand/logo-color-alt.png';
const LOGO_DARK = '/brand/logo-color.png';
const BRAND_PDF_URL = '/brand/dccf-manual.pdf';
const YOUTUBE_VIDEO_ID = 'iyVy8761cLE';

// Dominican Flag Ribbon SVG component
const DominicanRibbon = ({ className = '', flip = false }) => (
  <svg className={`absolute pointer-events-none ${className}`} viewBox="0 0 1200 200" fill="none" preserveAspectRatio="none">
    <path d={flip ? "M1200 100C900 40 600 160 300 80S-100 120 0 100" : "M0 100C300 40 600 160 900 80S1100 120 1200 100"} stroke="#CD1125" strokeWidth="3" opacity="0.12" />
    <path d={flip ? "M1200 130C850 70 550 180 250 100S-50 60 0 80" : "M0 70C350 130 650 20 950 100S1150 140 1200 120"} stroke="#002249" strokeWidth="2.5" opacity="0.10" />
    <path d={flip ? "M1200 60C950 120 650 30 350 90S50 150 0 130" : "M0 140C250 80 550 170 850 110S1150 50 1200 70"} stroke="white" strokeWidth="2" opacity="0.06" />
  </svg>
);

const FadeInSection = ({ children, className = '', delay = 0 }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 30 }} animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }} className={className}>
      {children}
    </motion.div>
  );
};

const AboutPage = () => {
  const objectives = [
    { number: '01', title: 'Economic Empowerment', text: 'Create pathways for Dominican entrepreneurs to access capital, mentorship, and market opportunities throughout Florida and beyond.' },
    { number: '02', title: 'Cultural Bridge', text: 'Strengthen the cultural and commercial ties between the Dominican Republic and the United States through strategic programs and initiatives.' },
    { number: '03', title: 'Business Advocacy', text: 'Represent and advocate for the interests of Dominican-owned businesses at local, state, and federal levels.' },
    { number: '04', title: 'Professional Development', text: 'Provide world-class educational resources, workshops, and leadership development programs for our members.' },
    { number: '05', title: 'Community Impact', text: 'Drive meaningful social and economic impact within the Dominican community across Florida.' },
    { number: '06', title: 'Global Network', text: 'Build an international network of partnerships that opens doors for Dominican businesses on the world stage.' },
  ];

  return (
    <div className="min-h-screen" data-testid="about-page">
      {/* ============ HERO — Premium Brand Opening ============ */}
      <section className="relative min-h-[85vh] flex items-center overflow-hidden bg-[#002249]" data-testid="about-hero">
        {/* Dominican flag-inspired abstract background */}
        <div className="absolute inset-0">
          {/* Deep navy base with subtle cross pattern inspired by flag */}
          <div className="absolute inset-0 bg-gradient-to-br from-[#001630] via-[#002249] to-[#001530]" />
          {/* Red accent — top right quadrant glow */}
          <div className="absolute top-0 right-0 w-1/2 h-1/2 bg-[#CD1125]/[0.04] blur-3xl" />
          {/* White cross arms — subtle */}
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[2px] h-full bg-white/[0.03]" />
          <div className="absolute top-1/2 left-0 -translate-y-1/2 w-full h-[2px] bg-white/[0.03]" />
          {/* Geometric grid */}
          <div className="absolute inset-0 opacity-[0.025]" style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.3) 1px, transparent 1px)', backgroundSize: '80px 80px' }} />
        </div>

        {/* Flowing ribbons */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <svg className="absolute -top-10 -right-20 w-[800px] h-[800px] opacity-[0.08]" viewBox="0 0 800 800" fill="none">
            <path d="M100 0C200 150 350 250 500 300S700 350 800 400" stroke="#CD1125" strokeWidth="3" className="ribbon-flow-1" />
            <path d="M0 100C150 180 300 280 450 340S650 400 800 450" stroke="white" strokeWidth="2" className="ribbon-flow-2" />
            <path d="M200 0C300 130 400 250 550 320S700 370 800 380" stroke="#002249" strokeWidth="2.5" className="ribbon-flow-3" />
          </svg>
          <svg className="absolute -bottom-20 -left-20 w-[600px] h-[600px] opacity-[0.06]" viewBox="0 0 600 600" fill="none">
            <path d="M0 500C120 400 250 320 380 280S520 240 600 180" stroke="#CD1125" strokeWidth="3" className="ribbon-flow-2" />
            <path d="M0 400C100 330 220 260 340 230S480 200 600 130" stroke="white" strokeWidth="2" className="ribbon-flow-3" />
          </svg>
        </div>

        <div className="relative z-10 container-custom pt-28 sm:pt-32 pb-20">
          <div className="max-w-4xl">
            <FadeInSection>
              {/* Logo mark */}
              <div className="mb-8 sm:mb-10">
                <Img variant="generic" src={LOGO_WHITE} alt="DCCF" className="h-20 sm:h-28 lg:h-32 object-contain" data-testid="about-hero-logo" />
              </div>
              <h1 className="text-3xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.08] mb-6 tracking-tight" data-testid="about-hero-headline">
                Building Bridges Between
                <span className="block mt-1" style={{ background: 'linear-gradient(90deg, #CD1125, #fff, #002249)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
                  Nations, Businesses &amp; Opportunity
                </span>
              </h1>
              <p className="text-white/60 text-base sm:text-lg lg:text-xl leading-relaxed max-w-2xl mb-10">
                The Dominican Chamber of Commerce of Florida stands as a premier institutional force — connecting 
                Dominican enterprise with the vast opportunities of the American market through vision, authority, and legacy.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/membership">
                  <Button size="lg" className="bg-[#CD1125] hover:bg-[#b00f20] text-white font-semibold px-8 rounded-sm shadow-lg shadow-[#CD1125]/20" data-testid="about-hero-join-btn">
                    Join the Chamber <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <a href="#brand-video">
                  <Button size="lg" variant="outline" className="border-2 border-white/30 text-white hover:bg-white/10 font-semibold px-8 rounded-sm bg-transparent" data-testid="about-hero-video-btn">
                    <Play className="mr-2 w-4 h-4" /> Watch Our Story
                  </Button>
                </a>
              </div>
            </FadeInSection>
          </div>
        </div>

        {/* Bottom edge accent */}
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-[#002249] via-[#CD1125] to-[#002249]" />
      </section>

      {/* ============ BRAND STORY — Who / What / Why ============ */}
      <section className="section-padding bg-white relative overflow-hidden" data-testid="brand-story">
        <DominicanRibbon className="top-0 left-0 w-full h-40 opacity-60" />
        <div className="container-custom relative z-10">
          <FadeInSection>
            <div className="text-center mb-14 sm:mb-16">
              <span className="text-xs sm:text-sm font-bold text-[#CD1125] tracking-[0.2em] uppercase mb-4 block">Our Identity</span>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#002249] max-w-3xl mx-auto leading-tight">
                More Than a Chamber. A Movement.
              </h2>
            </div>
          </FadeInSection>

          <div className="grid lg:grid-cols-3 gap-8 lg:gap-10">
            {[
              { tag: 'Who We Are', title: 'The Voice of Dominican Enterprise', text: 'We are the Dominican Chamber of Commerce of Florida — a premier institutional body that represents, empowers, and elevates the Dominican business community across the state. Our members range from emerging entrepreneurs to established industry leaders, united by a shared heritage and an unwavering drive for excellence.' },
              { tag: 'What We Stand For', title: 'Excellence Without Compromise', text: 'We stand for the power of unity, the value of strategic connection, and the belief that Dominican businesses deserve world-class representation. We champion ethical business practices, cross-cultural collaboration, and the kind of professional development that transforms industries.' },
              { tag: 'Why We Exist', title: 'A Bridge Built on Purpose', text: 'We exist because opportunity should know no borders. The Dominican diaspora in Florida represents an extraordinary force of talent, ambition, and resilience. Our role is to channel that force — providing the structure, advocacy, and network that turns potential into prosperity, for our members and our community.' },
            ].map((item, idx) => (
              <FadeInSection key={idx} delay={idx * 0.12}>
                <div className="group h-full" data-testid={`brand-story-${idx}`}>
                  <div className="h-full border border-slate-200 rounded-sm p-7 sm:p-8 hover:border-[#002249]/30 transition-all duration-300 hover:shadow-lg">
                    <span className="text-[11px] font-bold text-[#CD1125] tracking-[0.2em] uppercase mb-4 block">{item.tag}</span>
                    <h3 className="text-lg sm:text-xl font-bold text-[#002249] mb-4 leading-tight">{item.title}</h3>
                    <p className="text-slate-600 text-sm leading-relaxed">{item.text}</p>
                  </div>
                </div>
              </FadeInSection>
            ))}
          </div>
        </div>
      </section>

      {/* ============ MISSION / VISION / OBJECTIVES ============ */}
      <section className="relative overflow-hidden" data-testid="mission-vision-section">
        {/* Mission & Vision — side by side */}
        <div className="bg-[#002249] relative">
          <DominicanRibbon className="top-0 left-0 w-full h-48 opacity-80" />
          <div className="container-custom section-padding relative z-10">
            <FadeInSection>
              <div className="text-center mb-12 sm:mb-14">
                <span className="text-xs sm:text-sm font-bold text-[#CD1125] tracking-[0.2em] uppercase mb-4 block">Our Foundation</span>
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white">Mission &amp; Vision</h2>
              </div>
            </FadeInSection>

            <div className="grid md:grid-cols-2 gap-6 sm:gap-8 max-w-5xl mx-auto">
              <FadeInSection delay={0.1}>
                <div className="relative bg-white/[0.06] backdrop-blur-sm border border-white/10 rounded-sm p-7 sm:p-9 h-full" data-testid="mission-card">
                  <div className="absolute top-0 left-0 w-full h-1 bg-[#CD1125]" />
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-12 h-12 bg-[#CD1125] rounded-sm flex items-center justify-center flex-shrink-0">
                      <Target className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-xl sm:text-2xl font-bold text-white">Our Mission</h3>
                  </div>
                  <p className="text-white/70 text-sm sm:text-base leading-relaxed">
                    To empower Dominican entrepreneurs and professionals in Florida by providing world-class resources, 
                    strategic networking opportunities, and powerful advocacy that drives business growth and community 
                    development. We are the bridge — connecting ambition with opportunity across borders, industries, 
                    and generations.
                  </p>
                </div>
              </FadeInSection>
              <FadeInSection delay={0.2}>
                <div className="relative bg-white/[0.06] backdrop-blur-sm border border-white/10 rounded-sm p-7 sm:p-9 h-full" data-testid="vision-card">
                  <div className="absolute top-0 left-0 w-full h-1 bg-white/40" />
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-12 h-12 bg-white/20 rounded-sm flex items-center justify-center flex-shrink-0">
                      <Eye className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-xl sm:text-2xl font-bold text-white">Our Vision</h3>
                  </div>
                  <p className="text-white/70 text-sm sm:text-base leading-relaxed">
                    To be the most influential and respected Dominican business institution in the United States — 
                    recognized globally for our excellence in member services, our transformative community impact, 
                    and our role as the definitive bridge connecting Dominican enterprise with the limitless 
                    possibilities of the American and international markets.
                  </p>
                </div>
              </FadeInSection>
            </div>
          </div>
        </div>

        {/* Core Objectives */}
        <div className="bg-slate-50 relative">
          <DominicanRibbon className="bottom-0 left-0 w-full h-32 opacity-50" flip />
          <div className="container-custom section-padding relative z-10">
            <FadeInSection>
              <div className="text-center mb-12">
                <span className="text-xs sm:text-sm font-bold text-[#CD1125] tracking-[0.2em] uppercase mb-4 block">Strategic Pillars</span>
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#002249]">Core Objectives</h2>
              </div>
            </FadeInSection>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6 max-w-6xl mx-auto">
              {objectives.map((obj, idx) => (
                <FadeInSection key={idx} delay={idx * 0.08}>
                  <div className="bg-white border border-slate-200 rounded-sm p-6 hover:shadow-md transition-shadow h-full group" data-testid={`objective-${idx}`}>
                    <span className="text-3xl sm:text-4xl font-bold text-[#CD1125]/15 block mb-3 group-hover:text-[#CD1125]/25 transition-colors">{obj.number}</span>
                    <h4 className="text-base sm:text-lg font-bold text-[#002249] mb-2">{obj.title}</h4>
                    <p className="text-slate-600 text-sm leading-relaxed">{obj.text}</p>
                  </div>
                </FadeInSection>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ============ LOGO MEANING ============ */}
      <section className="section-padding bg-white relative overflow-hidden" data-testid="logo-meaning">
        <div className="container-custom">
          <FadeInSection>
            <div className="text-center mb-12 sm:mb-16">
              <span className="text-xs sm:text-sm font-bold text-[#CD1125] tracking-[0.2em] uppercase mb-4 block">Brand Identity</span>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#002249]">The Meaning Behind Our Identity</h2>
              <p className="text-slate-500 text-sm sm:text-base mt-3 max-w-2xl mx-auto">
                Every element of our visual identity was crafted with strategic intent — not decoration, but direction.
              </p>
            </div>
          </FadeInSection>

          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center max-w-6xl mx-auto">
            {/* Logo Display */}
            <FadeInSection delay={0.1}>
              <div className="relative">
                <div className="bg-gradient-to-br from-slate-50 to-white border border-slate-200 rounded-sm p-12 sm:p-16 flex items-center justify-center" data-testid="logo-display">
                  <Img variant="generic" src={LOGO_DARK} alt="DCCF Logo" className="w-full max-w-[300px] h-auto object-contain" />
                </div>
                {/* Subtle accent lines */}
                <div className="absolute -top-3 -left-3 w-12 h-12 border-t-2 border-l-2 border-[#CD1125]/30 rounded-tl-sm" />
                <div className="absolute -bottom-3 -right-3 w-12 h-12 border-b-2 border-r-2 border-[#002249]/30 rounded-br-sm" />
              </div>
            </FadeInSection>

            {/* Symbol Explanations */}
            <FadeInSection delay={0.2}>
              <div className="space-y-6" data-testid="logo-symbolism">
                {[
                  { title: 'The Coat of Arms', desc: 'Inspired by Dominican heraldic tradition, our emblem carries the weight of national pride and institutional authority. It signals that this is not just an organization — it is an institution with legacy.' },
                  { title: 'The Bridge Concept', desc: 'Our identity embodies the concept of connection — a bridge between the Dominican Republic and Florida, between tradition and innovation, between local enterprise and global opportunity.' },
                  { title: 'Colors of Two Nations', desc: 'The deep blue and bold red draw from the Dominican flag while resonating with the American palette — symbolizing the dual identity and cultural fusion that defines our community.' },
                  { title: 'Growth & Authority', desc: 'Every curve and line is designed to communicate trust, upward momentum, and institutional gravitas. This is a brand built for boardrooms and international stages alike.' },
                ].map((item, idx) => (
                  <div key={idx} className="flex gap-4">
                    <div className="w-1 bg-gradient-to-b from-[#CD1125] to-[#002249] rounded-full flex-shrink-0 mt-1" style={{ minHeight: '40px' }} />
                    <div>
                      <h4 className="font-bold text-[#002249] text-[15px] mb-1">{item.title}</h4>
                      <p className="text-slate-600 text-sm leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </FadeInSection>
          </div>
        </div>
      </section>

      {/* ============ COLOR SYSTEM ============ */}
      <section className="section-padding bg-[#002249] relative overflow-hidden" data-testid="color-system">
        <DominicanRibbon className="top-0 left-0 w-full h-48 opacity-70" />
        <div className="container-custom relative z-10">
          <FadeInSection>
            <div className="text-center mb-12 sm:mb-14">
              <span className="text-xs sm:text-sm font-bold text-[#CD1125] tracking-[0.2em] uppercase mb-4 block">Visual Language</span>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white">Our Colors</h2>
              <p className="text-white/50 text-sm sm:text-base mt-3 max-w-2xl mx-auto">
                Drawn from the Dominican flag, each color carries strategic meaning that guides every expression of our brand.
              </p>
            </div>
          </FadeInSection>

          <div className="grid md:grid-cols-3 gap-6 sm:gap-8 max-w-5xl mx-auto">
            {[
              { name: 'Rojo / Red', hex: '#CD1125', meaning: 'Strength, Passion & Action', desc: 'Red represents the blood, energy, and fire of our community. It symbolizes the passion Dominican entrepreneurs bring to every venture, the courage to cross borders, and the relentless drive that turns dreams into enterprises.', flag: 'From the Dominican flag — representing the blood of those who fought for freedom and sovereignty.' },
              { name: 'Azul / Blue', hex: '#002249', meaning: 'Trust, Stability & Leadership', desc: 'Blue carries the weight of authority and the calm of established trust. It is the color of deep waters and clear skies — representing the institutional reliability, leadership, and strategic vision that defines our Chamber.', flag: 'From the Dominican flag — representing God\'s protection and the ideals of progress and liberty.' },
              { name: 'Blanco / White', hex: '#FFFFFF', meaning: 'Transparency, Unity & Integrity', desc: 'White is the bridge between our two primary colors — just as our Chamber is the bridge between nations. It represents the purity of our mission, the transparency of our operations, and the unity that holds our community together.', flag: 'From the Dominican flag — the cross that unites the four quadrants, symbolizing salvation and unity.' },
            ].map((color, idx) => (
              <FadeInSection key={idx} delay={idx * 0.12}>
                <div className="bg-white/[0.06] backdrop-blur-sm border border-white/10 rounded-sm overflow-hidden h-full group" data-testid={`brand-color-${idx}`}>
                  <div className="h-28 sm:h-32 relative" style={{ backgroundColor: color.hex }}>
                    {color.hex === '#FFFFFF' && <div className="absolute inset-0 border-b border-white/10" />}
                    <div className="absolute bottom-3 left-4">
                      <span className={`text-xs font-mono font-bold ${color.hex === '#FFFFFF' ? 'text-[#002249]' : 'text-white'}`}>{color.hex}</span>
                    </div>
                  </div>
                  <div className="p-6">
                    <h4 className="text-lg font-bold text-white mb-1">{color.name}</h4>
                    <span className="text-[11px] font-bold text-[#CD1125] tracking-[0.15em] uppercase block mb-3">{color.meaning}</span>
                    <p className="text-white/60 text-sm leading-relaxed mb-4">{color.desc}</p>
                    <div className="border-t border-white/10 pt-3">
                      <p className="text-white/40 text-xs leading-relaxed italic">{color.flag}</p>
                    </div>
                  </div>
                </div>
              </FadeInSection>
            ))}
          </div>
        </div>
      </section>

      {/* ============ BRAND VIDEO — Premiere Experience ============ */}
      <section className="relative bg-black overflow-hidden" id="brand-video" data-testid="brand-video-section">
        {/* Cinematic gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#002249]/40 via-transparent to-[#002249]/40 pointer-events-none z-10" />
        <div className="container-custom section-padding relative z-20">
          <FadeInSection>
            <div className="text-center mb-10 sm:mb-12">
              <span className="text-xs sm:text-sm font-bold text-[#CD1125] tracking-[0.2em] uppercase mb-4 block">The Chamber Experience</span>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-3">Our Story in Motion</h2>
              <p className="text-white/40 text-sm sm:text-base max-w-xl mx-auto">
                Discover the vision, impact, and community that define the Dominican Chamber of Commerce of Florida.
              </p>
            </div>
          </FadeInSection>

          <FadeInSection delay={0.2}>
            <div className="max-w-5xl mx-auto">
              <div className="relative aspect-video rounded-sm overflow-hidden shadow-2xl shadow-black/50 border border-white/10" data-testid="brand-video-player">
                <iframe
                  src={`https://www.youtube.com/embed/${YOUTUBE_VIDEO_ID}?rel=0&modestbranding=1&color=white`}
                  title="The Chamber Experience"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="absolute inset-0 w-full h-full"
                />
              </div>
            </div>
          </FadeInSection>
        </div>
      </section>

      {/* ============ DOMINICAN FLAG TRIBUTE — Abstract ============ */}
      <section className="relative h-3 overflow-hidden">
        <div className="absolute inset-0 grid grid-cols-4">
          <div className="bg-[#002249]" />
          <div className="bg-[#CD1125]" />
          <div className="bg-[#002249]" />
          <div className="bg-[#CD1125]" />
        </div>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-4 h-4 bg-white rounded-full shadow-sm" />
        </div>
      </section>

      {/* ============ CTA ============ */}
      <section className="section-padding bg-slate-50 relative overflow-hidden" data-testid="about-cta">
        <DominicanRibbon className="top-0 left-0 w-full h-40 opacity-40" />
        <div className="container-custom relative z-10">
          <FadeInSection>
            <div className="max-w-3xl mx-auto text-center">
              <Img variant="generic" src={LOGO_DARK} alt="DCCF" className="h-16 sm:h-20 mx-auto mb-8 object-contain" />
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#002249] mb-5">
                Join an Institution Built for Leaders
              </h2>
              <p className="text-slate-600 text-base sm:text-lg mb-8 leading-relaxed">
                Whether you're an established business or an emerging entrepreneur, the Dominican Chamber of 
                Commerce of Florida is your gateway to growth, connection, and lasting impact.
              </p>
              <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4">
                <Link to="/membership">
                  <Button size="lg" className="w-full sm:w-auto bg-[#CD1125] hover:bg-[#b00f20] text-white font-semibold px-8 rounded-sm" data-testid="about-cta-join">
                    Become a Member <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <Link to="/leadership">
                  <Button size="lg" variant="outline" className="w-full sm:w-auto border-2 border-[#002249] text-[#002249] hover:bg-[#002249] hover:text-white font-semibold px-8 rounded-sm" data-testid="about-cta-leadership">
                    Meet Our Leadership
                  </Button>
                </Link>
                <Link to="/brand-resources">
                  <Button size="lg" variant="outline" className="w-full sm:w-auto border border-slate-300 text-slate-600 hover:bg-slate-100 font-medium px-6 rounded-sm" data-testid="about-cta-brand">
                    Brand Resources <ExternalLink className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
              </div>
            </div>
          </FadeInSection>
        </div>
      </section>
      {/* Leadership Signature — Founder */}
      <section className="py-12 sm:py-16 px-5 sm:px-8 bg-[#FAFAF8]">
        <div className="max-w-3xl mx-auto">
          <LeadershipSignature title="Founder" label="Our Founder" blurb="The Dominican Chamber of Commerce of Florida was founded with a clear vision: to unite, empower, and elevate Dominican businesses and professionals across the state." />
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
